import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import RatingCell from "@/components/RatingCell";
import { ARMIES, factionIcon, DEFAULT_CELL } from "@/lib/game";
import { Swords, Info, Save, Trash2, Users, Download, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function FactionRatingsTab({ setup, updateSetup }) {
  const yours = setup.players.filter((p) => p.team === setup.you_are);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");

  const loadPresets = () => api.presets().then(setPresets).catch(() => {});
  useEffect(() => { loadPresets(); }, []);

  const savePreset = async () => {
    if (yours.length === 0) return toast.error("Add players to your team first");
    const name = presetName.trim() || setup[setup.you_are === "A" ? "team_a_name" : "team_b_name"] || "My Team";
    const players = yours.map((p) => ({
      name: p.name, faction: p.faction, ratings: setup.faction_ratings?.[p.id] || {},
    }));
    await api.savePreset({ name, country: "", players });
    setPresetName("");
    await loadPresets();
    toast.success(`Saved team preset: ${name}`);
  };

  const loadPreset = (preset) => {
    const youKey = setup.you_are;
    const others = setup.players.filter((p) => p.team !== youKey);
    const fr = { ...(setup.faction_ratings || {}) };
    // drop old ratings for current your-players
    for (const p of setup.players.filter((x) => x.team === youKey)) delete fr[p.id];
    const newPlayers = (preset.players || []).map((p) => {
      const id = uid();
      if (p.ratings && Object.keys(p.ratings).length) fr[id] = { ...p.ratings };
      return { id, name: p.name, faction: p.faction, team: youKey, lists: [] };
    });
    updateSetup({
      ...setup,
      [youKey === "A" ? "team_a_name" : "team_b_name"]: preset.name || "My Team",
      players: [...others, ...newPlayers],
      faction_ratings: fr,
    });
    toast.success(`Loaded preset: ${preset.name}`);
  };

  const deletePreset = async (id) => { await api.delPreset(id); setPresets((s) => s.filter((x) => x.id !== id)); };

  const getCell = (pid, army) => setup.faction_ratings?.[pid]?.[army] || { ...DEFAULT_CELL, source: "default" };
  const setCell = (pid, army, val) => {
    const fr = { ...(setup.faction_ratings || {}) };
    fr[pid] = { ...(fr[pid] || {}), [army]: val };
    updateSetup({ ...setup, faction_ratings: fr });
  };
  const clearCell = (pid, army) => {
    const fr = { ...(setup.faction_ratings || {}) };
    if (fr[pid]) { fr[pid] = { ...fr[pid] }; delete fr[pid][army]; }
    updateSetup({ ...setup, faction_ratings: fr });
  };
  const resetAllRatings = () => {
    if (!window.confirm("Reset every rating to Little Win and clear all notes?")) return;
    updateSetup({ ...setup, faction_ratings: {} });
    toast.success("All ratings reset to Little Win");
  };

  const presetsCard = (
    <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5" data-testid="team-presets-card">
      <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-1 flex items-center gap-2">
        <Users className="h-4 w-4 text-amber-400" /> Team Presets
      </h3>
      <p className="text-xs text-slate-500 mb-3">Save your whole roster (players, armies &amp; these ratings) as a reusable preset, then reload it for any future event.</p>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input data-testid="preset-name-input" value={presetName} onChange={(e) => setPresetName(e.target.value)}
          placeholder="Preset name (e.g. USA Pinnacles)" className="bg-slate-950/60 border-slate-700 flex-1" />
        <Button data-testid="save-preset-button" onClick={savePreset}
          className="bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold">
          <Save className="h-4 w-4 mr-2" /> Save current team
        </Button>
      </div>
      {presets.length > 0 && (
        <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
          {presets.map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center gap-3" data-testid="preset-row">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-100 truncate">{p.name}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                  {(p.players || []).map((pl, j) => (
                    <span key={`${pl.name}-${j}`} className="text-[11px] text-slate-400 flex items-center gap-1">
                      <img src={factionIcon(pl.faction)} alt="" className="h-3.5 w-3.5 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
                      {pl.name}
                    </span>
                  ))}
                </div>
              </div>
              <Button data-testid="load-preset-button" onClick={() => loadPreset(p)} size="sm"
                className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 font-semibold shrink-0">
                <Download className="h-3.5 w-3.5 mr-1" /> Load
              </Button>
              <Button data-testid="delete-preset-button" onClick={() => deletePreset(p.id)} variant="ghost" size="icon"
                className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  if (yours.length === 0) {
    return (
      <div className="space-y-5">
        {presetsCard}
        <Card className="bg-[#121824]/70 border-slate-800 p-10 text-center">
          <Swords className="h-8 w-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-semibold">Add players to your team first.</p>
          <p className="text-slate-500 text-sm mt-1">Claim your team from an event or add players in <b>My Team</b>, or load a preset above.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {presetsCard}
      <Card className="bg-[#121824]/70 border-slate-800 p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-400 flex-1">
          Every matchup starts at <b className="text-emerald-300">Little Win</b> — just change the exceptions. These become the
          auto-fill defaults in the opponent <b>Matchup Matrix</b>; add a <b>note</b> to any cell for conditions worth remembering.
        </p>
        <Button data-testid="reset-ratings-button" onClick={resetAllRatings} variant="outline"
          className="border-slate-700 bg-transparent text-slate-300 hover:text-red-400 shrink-0">
          <RotateCcw className="h-4 w-4 mr-2" /> Reset to Little Wins
        </Button>
      </Card>

      <Card className="bg-[#121824]/70 border-slate-800 p-3 sm:p-4 overflow-x-auto">
        <div className="min-w-max">
          <div
            className="grid gap-1.5"
            style={{ gridTemplateColumns: `160px repeat(${ARMIES.length}, 54px)` }}
          >
            <div className="sticky left-0 z-10 bg-[#121824]" />
            {ARMIES.map((a) => (
              <div key={a.name} className="flex flex-col items-center justify-end pb-1.5 h-[88px]" title={a.name}>
                <img
                  src={factionIcon(a.name)}
                  alt={a.name}
                  className="h-7 w-7 object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <span className="text-[8px] text-slate-500 leading-tight text-center mt-1 w-full break-words">
                  {a.abbr || a.name.split(" ")[0]}
                </span>
              </div>
            ))}

            {yours.map((p) => (
              <RatingRow key={p.id} p={p} getCell={getCell} setCell={setCell} clearCell={clearCell} />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function RatingRow({ p, getCell, setCell, clearCell }) {
  return (
    <>
      <div className="sticky left-0 z-10 bg-[#121824] flex flex-col justify-center pr-2 border-r border-slate-800">
        <p className="text-sm font-semibold text-amber-300 truncate">{p.name || "—"}</p>
        <p className="text-[10px] text-slate-500 font-mono truncate">{p.faction}</p>
      </div>
      {ARMIES.map((a) => (
        <RatingCell
          key={a.name}
          testid="faction-rating-cell"
          armyName={a.name}
          cellId={`fr:${p.id}:${a.name}`}
          cell={getCell(p.id, a.name)}
          onSet={(v) => setCell(p.id, a.name, v)}
          onClear={() => clearCell(p.id, a.name)}
        />
      ))}
    </>
  );
}
